import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { Menu, X, Phone, Brain } from "lucide-react";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/conditions", label: "Conditions" },
  { to: "/medicines", label: "Medicines Guide" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      data-testid="main-header"
      className={`sticky top-0 z-50 backdrop-blur-xl border-b transition-all duration-300 ${
        scrolled ? "bg-background/95 shadow-sm" : "bg-background/80"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <Brain className="w-7 h-7 text-primary" />
            <div className="leading-tight">
              <span className="font-heading font-bold text-lg text-foreground block">Dr. Ambrish Singal</span>
              <span className="text-xs text-muted-foreground hidden sm:block">Psychiatrist | De-addiction Specialist | Sexologist</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  location.pathname === link.to
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a href="tel:+919915096633" className="hidden sm:flex" data-testid="header-call-btn">
              <Button size="sm" className="gap-2 rounded-full">
                <Phone className="w-4 h-4" />
                <span className="hidden md:inline">Book Appointment</span>
              </Button>
            </a>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 rounded-md hover:bg-accent"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t bg-background" data-testid="mobile-menu">
          <nav className="px-4 py-4 space-y-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`block px-3 py-2.5 rounded-md text-sm font-medium ${
                  location.pathname === link.to
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a href="tel:+919915096633" className="block px-3 py-2.5">
              <Button className="w-full gap-2 rounded-full">
                <Phone className="w-4 h-4" /> Book Appointment
              </Button>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer data-testid="main-footer" className="border-t bg-secondary/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Brain className="w-6 h-6 text-primary" />
              <span className="font-heading font-bold text-lg">Dr. Ambrish Singal</span>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">
              MBBS, MD Psychiatry, FIPS, MAPA. Psychiatrist, Drug De-addiction Specialist & Sexologist with 28+ years of experience. Sector 79, Sahibzada Ajit Singh Nagar.
            </p>
            <p className="text-sm text-muted-foreground">
              Punjab Medical Council Reg. No. 29107
            </p>
          </div>

          <div>
            <h4 className="font-heading font-semibold mb-4">Services</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/conditions/depression" className="hover:text-primary transition-colors">Depression Treatment</Link></li>
              <li><Link to="/conditions/anxiety" className="hover:text-primary transition-colors">Anxiety Treatment</Link></li>
              <li><Link to="/conditions/alcohol-addiction" className="hover:text-primary transition-colors">Alcohol De-addiction</Link></li>
              <li><Link to="/conditions/erectile-dysfunction" className="hover:text-primary transition-colors">Sexual Health</Link></li>
              <li><Link to="/conditions/dementia" className="hover:text-primary transition-colors">Dementia Care</Link></li>
              <li><Link to="/conditions/adhd" className="hover:text-primary transition-colors">ADHD Treatment</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/about" className="hover:text-primary transition-colors">About Dr. Singal</Link></li>
              <li><Link to="/services" className="hover:text-primary transition-colors">All Services</Link></li>
              <li><Link to="/medicines" className="hover:text-primary transition-colors">Medicines Guide</Link></li>
              <li><Link to="/contact" className="hover:text-primary transition-colors">Book Appointment</Link></li>
              <li><Link to="/location/mohali" className="hover:text-primary transition-colors">Mohali Clinic</Link></li>
              <li><Link to="/location/chandigarh" className="hover:text-primary transition-colors">Chandigarh</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold mb-4">Contact</h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li>
                <strong className="text-foreground block">Clinic</strong>
                Opposite Housefed Society 2, 704, HT Towers, Sector 79, Sahibzada Ajit Singh Nagar, Punjab 140308
              </li>
              <li>
                <a href="tel:+919915096633" className="hover:text-primary transition-colors">+91-99150 96633 (Call)</a>
              </li>
              <li>
                <a href="https://wa.me/919815333660" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">+91-98153 33660 (WhatsApp)</a>
              </li>
              <li>
                <a href="mailto:drambrishsingal@gmail.com" className="hover:text-primary transition-colors">drambrishsingal@gmail.com</a>
              </li>
            </ul>
          </div>
        </div>

        {/* Trust Badge */}
        <div className="border-t pt-8 mb-8 text-center" data-testid="trust-section">
          <p className="text-sm text-muted-foreground">
            <span className="text-accent font-semibold">28+ Years</span> of trusted psychiatric care in the Tricity region
          </p>
        </div>

        <div className="border-t pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} Dr. Ambrish Singal. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/location/mohali" className="hover:text-primary">Mohali</Link>
            <Link to="/location/chandigarh" className="hover:text-primary">Chandigarh</Link>
            <Link to="/location/panchkula" className="hover:text-primary">Panchkula</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
